import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import usePrevious from "../../common/hooks/usePrevious";
import { Nullable } from "../../common/types/types";

interface IPreloadContextValue {
	progress: number;
	isLoading: boolean;
	preloadImages: (urls: Nullable<string>[]) => void; // Changed to void, as the async logic is in useEffect
}

const PreloadContext = createContext<IPreloadContextValue | undefined>(undefined);

interface IPreloadProviderProps {
	children: React.ReactNode;
}

export const PreloadProvider: React.FC<IPreloadProviderProps> = ({ children }) => {
	const [progress, setProgress] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [preLoadImages, setPreLoadImages] = useState<string[]>([]);
	const prevPreLoadImages = usePrevious(preLoadImages);

	// Extract image loading logic to reduce nesting
	const loadImage = useCallback((url: string): Promise<void> => {
		return new Promise<void>((resolve) => {
			const img = new Image();
			const handleImageComplete = () => resolve();

			img.onload = handleImageComplete;
			img.onerror = handleImageComplete;
			img.src = url;
		});
	}, []);

	// Extract progress update logic
	const updateProgress = useCallback((loadedCount: number, totalCount: number) => {
		setProgress(loadedCount / totalCount);
	}, []);

	useEffect(() => {
		if (prevPreLoadImages && prevPreLoadImages?.length === 0 && preLoadImages?.length > 0) {
			setIsLoading(true);
			setProgress(0);

			const validUrls = preLoadImages.filter(Boolean);
			const totalUrls = validUrls.length;
			let loadedCount = 0;
			const currentPreloadImages = [...preLoadImages];

			const promises = validUrls.map((url) => {
				return loadImage(url).then(() => {
					loadedCount++;
					updateProgress(loadedCount, totalUrls);
				});
			});

			Promise.all(promises).then(() => {
				setPreLoadImages((prev) => prev.filter((url) => !currentPreloadImages.includes(url)));
			});
		}
	}, [preLoadImages, prevPreLoadImages, loadImage, updateProgress]);

	useEffect(() => {
		if (prevPreLoadImages && prevPreLoadImages.length > 0 && preLoadImages?.length === 0) {
			setIsLoading(false);
			setProgress(1);
		}
	}, [preLoadImages, prevPreLoadImages]);

	const preloadImages = useCallback((urls: Nullable<string>[]) => {
		setPreLoadImages((prev) => [...prev, ...urls.filter(Boolean)]);
	}, []);

	const value: IPreloadContextValue = useMemo(
		() => ({
			progress,
			isLoading,
			preloadImages,
		}),
		[progress, isLoading, preloadImages]
	);

	return <PreloadContext.Provider value={value}>{children}</PreloadContext.Provider>;
};

export const usePreload = () => {
	const context = useContext(PreloadContext);

	if (!context) {
		throw new Error("usePreload must be used within a PreloadProvider");
	}

	return context;
};
